import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Chart } from 'angular-highcharts';
import { add, compareAsc, format, isAfter, isBefore } from 'date-fns';
import { PointOptionsObject, Tick } from 'highcharts';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { DriversService } from 'src/app/root/drivers.service';
import { AuthService } from '../../root/auth.service';
import { DriverShiftOverviewRegion, LoHiDriverShiftOverview } from '../../types';

@Component({
  selector: 'lohi-shift-chart-v2',
  templateUrl: './shift-chart-v2.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShiftChartV2Component implements OnInit, OnDestroy, OnChanges {
  public destroy$$ = new Subject();
  public clonedEarnings: LoHiDriverShiftOverview[];
  public subtext: {
    content: string;
    classes: string;
  };
  public activeTick: Tick;
  @Input() public earningsPerDay: DriverShiftOverviewRegion;
  @Input() public showDemand = false;
  @Output() public barSelected = new EventEmitter<Date>();

  public get format(): string {
    return DATETIME_FORMAT;
  }

  public get timeFormat(): string {
    return TIME_FORMAT;
  }

  public get labels(): Array<string> {
    if (!this.earningsPerDay) {
      return [];
    }
    return this.clonedEarnings.slice().map((value) => {
      const start = new Date(value.timestamp);
      const end = add(new Date(value.timestamp), { hours: 1 });
      if (isAfter(new Date(), start) && isBefore(new Date(), end)) {
        return 'Now';
      }
      return format(start, this.format, {
        useAdditionalDayOfYearTokens: true,
        useAdditionalWeekYearTokens: true,
      });
    });
  }

  public get belowCommitment(): number {
    return this.clonedEarnings.filter((value) => !!value.belowCommitment).length;
  }

  public chart: Chart;

  constructor(private driversService: DriversService, private authService: AuthService) {
    this.driversService.shiftFilterDate$.pipe(takeUntil(this.destroy$$)).subscribe((date) => {
      if (!this.chart) {
        return;
      }
      if (!date) {
        this.activeTick.label.removeClass('font-bold').css({
          fontSize: '11px',
          color: '#666666',
        });
      }
    });
  }

  public ngOnInit() {
    this.setupChart();
  }

  private setupChart() {
    this.clonedEarnings = this.earningsPerDay?.overviews
      .slice()
      .sort((a, b) => compareAsc(new Date(a.timestamp), new Date(b.timestamp)));
    this.subtext = getSubtext(this.clonedEarnings);
    const labels = this.labels;

    this.chart = new Chart({
      chart: {
        type: 'column',
      },
      title: null,
      credits: {
        enabled: false,
      },
      yAxis: [
        {
          labels: {
            enabled: true,
          },
          title: { text: 'Drivers' },
        },
      ],
      plotOptions: {
        series: {
          stacking: 'normal',
          label: {
            enabled: true,
          },
          events: {
            click: (event) => {
              if (event?.point?.index <= this.clonedEarnings.length - 1) {
                const tick = this.chart.ref.xAxis[0]?.ticks[event.point.index];
                if (tick === this.activeTick) {
                  this.barSelected.emit(null);
                  this.activeTick = null;
                } else {
                  if (this.activeTick) {
                    this.activeTick.label?.removeClass('font-bold').css({
                      fontSize: '11px',
                      color: '#666666',
                    });
                  }
                  this.activeTick = tick;
                  this.activeTick.label?.addClass('font-bold').css({
                    fontSize: '20px',
                    color: '#0e4eaf',
                  });
                  this.barSelected.emit(new Date(this.clonedEarnings[event.point.index].timestamp));
                }
              }
            },
          },
        },
      },
      xAxis: {
        type: 'category',
        categories: labels,
        visible: true,
        labels: {
          autoRotation: [],
          rotation: 90,
          formatter: (that) => {
            if (that.value === 'Now') {
              return that.value;
            }
            return (that.value as string).split(' ').slice(1, 4).join(' ');
          },
        },
      },
      series: [],
    });

    const s: Array<[number | string, number | null] | PointOptionsObject> = [];
    const s2: Array<[number | string, number | null] | PointOptionsObject> = [];
    const s3: Array<[number | string, number | null] | PointOptionsObject> = [];
    const s4: Array<[number | string, number | null] | PointOptionsObject> = [];
    for (const date of this.clonedEarnings.slice()) {
      let label = format(new Date(date.timestamp), this.format, {
        useAdditionalWeekYearTokens: true,
        useAdditionalDayOfYearTokens: true,
      });
      const start = new Date(date.timestamp);
      const end = add(new Date(date.timestamp), { hours: 1 });
      if (isAfter(new Date(), start) && isBefore(new Date(), end)) {
        label = 'Now';
      }
      s.push({
        label,
        y: date.scheduledShiftCount,
      });
      s2.push({
        label,
        y: date.actualShiftCount,
      });

      if (!!date.belowCommitment) {
        s3.push({
          label,
          y: date.commitment,
          marker: {
            radius: 15,
          },
          z: 1000,
          custom: {
            number: date.belowCommitment.index,
          },
        });
      } else {
        s3.push({
          label,
          y: date.commitment,
          marker: {
            enabled: false,
          },
        });
      }

      s4.push({
        label,
        y: date.demand,
      });
    }

    this.authService.userInfo$.pipe(take(1)).subscribe((userInfo) => {
      if (userInfo.accountRelationship === 'saas') {
        this.chart.addSeries(
          {
            name: 'Commitment',
            type: 'areaspline',
            color: '#CB2026',
            fillOpacity: 0.3,
            data: s3,
            dataLabels: {
              inside: true,
              enabled: true,
              borderWidth: 0,
              borderColor: 'transparent',
              align: 'center',
              verticalAlign: 'middle',
              color: 'white',
              style: {
                textOutline: '',
              },
              useHTML: true,
              formatter(options) {
                return this.point.options?.custom?.number || null;
              },
            },
            zIndex: -1,
            dashStyle: 'Dash',
          },
          true,
          true,
        );
      }
    });

    this.chart.addSeries(
      {
        name: 'Estimated Future Shifts',
        type: 'column',
        color: {
          linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
          stops: [
            [0, '#070707'],
            [1, '#3e3e52'],
          ],
        },
        borderWidth: 0,
        data: s,
        stack: 1,
      },
      true,
      true,
    );
    this.chart.addSeries(
      {
        name: 'In Progress Shifts',
        type: 'column',
        color: '#448AFF',
        borderWidth: 0,
        data: s2,
        stack: 1,
      },
      true,
      true,
    );
    if (this.showDemand) {
      this.chart.addSeries(
        {
          name: 'Demand',
          type: 'spline',
          color: '#0e4eaf',
          data: s4,
          yAxis: 0,
          marker: {
            enabled: false,
          },
          stack: 2,
          borderWidth: 0,
          dashStyle: 'Dash',
        },
        true,
        true,
      );
    }
  }

  public ngOnDestroy(): void {
    this.destroy$$.next();
    this.destroy$$.unsubscribe();
  }

  public emitDateString(input: string) {
    this.emitDate(new Date(input));
  }

  public emitDate(timestamp: Date) {
    this.barSelected.emit(timestamp);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.setupChart();
  }
}

const DATETIME_FORMAT = 'M/dd/yyyy ha';
const TIME_FORMAT = 'ha';

function getSubtext(
  entries: LoHiDriverShiftOverview[],
): {
  content: string;
  classes: string;
} {
  let isAllGood = true;
  for (const entry of entries) {
    const totalShifts = entry.actualShiftCount + entry.scheduledShiftCount;
    if (totalShifts < entry.commitment || totalShifts < entry.demand) {
      isAllGood = false;
      break;
    }
  }

  if (isAllGood) {
    return {
      content: 'All Good!',
      classes: 'text-gray-500',
    };
  }

  let canIncreaseRevenue = false;
  let consecutiveMatches = 0;
  for (const entry of entries) {
    const totalShifts = entry.actualShiftCount + entry.scheduledShiftCount;
    if (entry.commitment < entry.demand && entry.commitment < totalShifts && totalShifts < entry.demand) {
      consecutiveMatches += 1;
    } else {
      // reset
      consecutiveMatches = 0;
    }
    if (consecutiveMatches >= 5) {
      canIncreaseRevenue = true;
      break;
    }
  }

  if (canIncreaseRevenue) {
    return {
      content: 'Add Drivers to Increase Revenue',
      classes: 'text-orange-500',
    };
  }

  let hasWarning = false;
  consecutiveMatches = 0;
  for (const entry of entries.slice(0, 24)) {
    const totalShifts = entry.actualShiftCount + entry.scheduledShiftCount;
    const minTarget = entry.commitment;
    if (totalShifts < minTarget) {
      consecutiveMatches += 1;
    } else {
      // reset
      consecutiveMatches = 0;
    }
    if (consecutiveMatches >= 2) {
      hasWarning = true;
      break;
    }
  }

  if (hasWarning) {
    return {
      content: 'Add Drivers to Meet Commitment',
      classes: 'text-red-500',
    };
  }
}
